import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaHeart, FaGithub, FaLinkedin, FaEnvelope } from 'react-icons/fa';

const FooterContainer = styled.footer`
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(0, 212, 255, 0.2);
  padding: 3rem 2rem 1rem;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const FooterSection = styled(motion.div)`
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const FooterTitle = styled.h3`
  color: #00d4ff;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const FooterText = styled.p`
  color: #b8b8b8;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media (min-width: 768px) {
    justify-content: flex-start;
  }
`;

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const QuickLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const QuickLink = styled(motion.a)`
  color: #b8b8b8;
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;

  &:hover {
    color: #00d4ff;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContactItem = styled.div`
  color: #b8b8b8;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;

  @media (min-width: 768px) {
    justify-content: flex-start;
  }

  svg {
    color: #00d4ff;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(0, 212, 255, 0.1);
  padding-top: 2rem;
  text-align: center;
`;

const Copyright = styled(motion.p)`
  color: #b8b8b8;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  svg {
    color: #ff6b6b;
  }
`;

const BackToTop = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  z-index: 1000;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 212, 255, 0.3);
  }
`;

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <FooterContainer>
        <FooterContent>
          <FooterSection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <FooterTitle>Satish Mukhekar</FooterTitle>
            <FooterText>
              Blockchain Developer passionate about building innovative decentralized solutions. 
              Specializing in smart contracts, DeFi protocols, and Web3 technologies.
            </FooterText>
            <SocialLinks>
              <SocialLink
                href="https://github.com/satish-mukhekar"
                target="_blank"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaGithub />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/in/mahesh-satish-shinde-b8a01a2a0/"
                target="_blank"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaLinkedin />
              </SocialLink>
              <SocialLink
                href="mailto:satishmukhekar4@gmail.com"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaEnvelope />
              </SocialLink>
            </SocialLinks>
          </FooterSection>

          <FooterSection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <FooterTitle>Quick Links</FooterTitle>
            <QuickLinks>
              <QuickLink
                onClick={() => scrollToSection('home')}
                whileHover={{ x: 5 }}
              >
                Home
              </QuickLink>
              <QuickLink
                onClick={() => scrollToSection('about')}
                whileHover={{ x: 5 }}
              >
                About
              </QuickLink>
              <QuickLink
                onClick={() => scrollToSection('skills')}
                whileHover={{ x: 5 }}
              >
                Skills
              </QuickLink>
              <QuickLink
                onClick={() => scrollToSection('experience')}
                whileHover={{ x: 5 }}
              >
                Experience
              </QuickLink>
              <QuickLink
                onClick={() => scrollToSection('projects')}
                whileHover={{ x: 5 }}
              >
                Projects
              </QuickLink>
              <QuickLink
                onClick={() => scrollToSection('contact')}
                whileHover={{ x: 5 }}
              >
                Contact
              </QuickLink>
            </QuickLinks>
          </FooterSection>

          <FooterSection
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <FooterTitle>Contact Info</FooterTitle>
            <ContactInfo>
              <ContactItem>
                <FaEnvelope />
                <span>satishmukhekar4@gmail.com</span>
              </ContactItem>
              <ContactItem>
                <span>📱</span>
                <span>+91-9975990287</span>
              </ContactItem>
              <ContactItem>
                <span>📍</span>
                <span>Pune,Maharastra,India</span>
              </ContactItem>
            </ContactInfo>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            © 2025 Satish Mukhekar. Made with <FaHeart /> for the blockchain community.
          </Copyright>
        </FooterBottom>
      </FooterContainer>

      <BackToTop
        onClick={scrollToTop}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        ↑
      </BackToTop>
    </>
  );
};

export default Footer;